import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  apiAdminGetMasterAgents,
  apiAdminGetMasters,
  ApiRequestError,
  type AdminPartner,
} from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { ArrowLeft, AlertCircle, RefreshCw, ChevronRight, Lock } from 'lucide-react'
import { useT } from '@/contexts/language'

function statusVariant(s: string): 'success' | 'destructive' | 'warning' | 'outline' {
  if (s === 'active') return 'success'
  if (s === 'suspended') return 'destructive'
  if (s === 'inactive') return 'warning'
  return 'outline'
}
const ratePct = (r: string | null) => (r === null || r === '' ? '—' : `${(parseFloat(r) * 100).toFixed(0)}%`)

export default function AdminMasterAgentsPage() {
  const t = useT()
  const navigate = useNavigate()
  const { masterId } = useParams<{ masterId: string }>()
  const [agents, setAgents] = useState<AdminPartner[]>([])
  const [masterName, setMasterName] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!masterId) return
    setLoading(true)
    setError(null)
    try {
      const [ags, masters] = await Promise.all([apiAdminGetMasterAgents(masterId), apiAdminGetMasters()])
      setAgents(ags)
      setMasterName(masters.find((m) => m.id === masterId)?.name ?? masterId)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [masterId, t.common.error])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/masters')}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t.nav.masters}
        </Button>
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {loading ? (
            <Skeleton className="h-5 w-40" />
          ) : (
            <>
              <h1 className="font-mono text-sm font-bold uppercase tracking-wider text-foreground truncate">
                {masterName}
              </h1>
              <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" /> ดูอย่างเดียว
              </span>
            </>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={loading}
          className="h-8 gap-1.5 font-mono text-xs shrink-0"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          {t.common.refresh}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            เอเจนต์ในสายนี้
          </span>
          {!loading && <span className="font-mono text-xs text-secondary-foreground/60">{agents.length} รายการ</span>}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="ml-auto h-5 w-16" />
              </div>
            ))}
          </div>
        ) : agents.length === 0 ? (
          <div className="py-12 text-center">
            <p className="font-mono text-sm text-muted-foreground">มาสเตอร์นี้ยังไม่มีเอเจนต์</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เอเจนต์</TableHead>
                <TableHead className="text-right">หุ้นเอเจนต์</TableHead>
                <TableHead className="text-right">หุ้นมาสเตอร์</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="w-8" aria-hidden="true" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((a) => (
                <TableRow
                  key={a.id}
                  onClick={() => navigate(`/admin/masters/${masterId}/agents/${a.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/admin/masters/${masterId}/agents/${a.id}`)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`${a.name} — สมาชิก`}
                  className="cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <TableCell>
                    <p className="font-mono text-sm font-semibold">{a.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{a.code}</p>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {ratePct(a.commissionRate)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {ratePct(a.masterShareRate)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(a.status)} className="text-[10px]">
                      {a.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-8 text-muted-foreground">
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
